/**
 * Process a file and create a preview URL
 * @param {File} file - The image file
 * @returns {Promise<string>} Preview URL as data URL
 */
export const createFilePreview = (file) => {
    return new Promise((resolve, reject) => {
        if (!file.type.startsWith('image/')) {
            reject(new Error('File must be an image'));
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            resolve(e.target.result);
        };
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(file);
    });
};

/**
 * Find an existing photo by its template ID or name (fallback)
 * @param {Array} existingPhotos - Array of existing photos
 * @param {Object} photoTemplate - Photo template with id and name properties
 * @returns {Object|undefined} The found photo or undefined
 */
export const findPhotoByTemplate = (existingPhotos, photoTemplate, subTag = null) => {
    // First try to match by template_id (if photo was uploaded with ID)
    if (photoTemplate.id) {
        const matchById = existingPhotos.find(photo => {
            if (photo.template_id !== photoTemplate.id) return false;
            if (subTag !== null) return (photo.sub_tag || null) === subTag;
            return true;
        });
        if (matchById) return matchById;
    }

    // Fallback to exact name matching for backwards compatibility with old photos
    const exactMatch = existingPhotos.find(photo => {
        if (photo.photo_name !== photoTemplate.name) return false;
        if (subTag !== null) return (photo.sub_tag || null) === subTag;
        return true;
    });
    if (exactMatch) return exactMatch;

    // If no exact match, try fuzzy matching (handles "Side" vs "Door" changes)
    // Extract the key parts: angle type and direction
    const templateNameNormalized = photoTemplate.name
        .toLowerCase()
        .replace(/\(.*?\)/g, '') // Remove parentheses content
        .replace(/\s+/g, ' ')
        .trim();

    return existingPhotos.find(photo => {
        const photoNameNormalized = photo.photo_name
            .toLowerCase()
            .replace(/\(.*?\)/g, '') // Remove parentheses content
            .replace(/\s+/g, ' ')
            .trim();

        if (photoNameNormalized !== templateNameNormalized) return false;
        if (subTag !== null) return (photo.sub_tag || null) === subTag;
        return true;
    });
};

/**
 * Find an existing photo by its name (legacy - kept for compatibility)
 * @param {Array} existingPhotos - Array of existing photos
 * @param {string} photoName - Name to search for
 * @returns {Object|undefined} The found photo or undefined
 */
export const findPhotoByName = (existingPhotos, photoName) => {
    return existingPhotos.find(photo => photo.photo_name === photoName);
};

/**
 * Validate image file
 * @param {File} file - The file to validate
 * @returns {Object} Validation result { valid: boolean, error?: string }
 */
export const validateImageFile = (file) => {
    if (!file) {
        return { valid: false, error: 'No file provided' };
    }

    if (!file.type.startsWith('image/')) {
        return { valid: false, error: 'File must be an image' };
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
        return { valid: false, error: 'File size must be less than 10MB' };
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
        return { valid: false, error: 'Only JPEG, PNG, and WebP images are allowed' };
    }

    return { valid: true };
};

/**
 * Format photo metadata for upload
 * @param {string} photoName - Photo name
 * @param {Object} template - Photo template with id, category, description, tags
 * @returns {Object} Formatted metadata
 */
export const formatPhotoMetadata = (photoName, template) => {
    const metadata = {
        photoName,
        category: template.category,
        description: template.description,
        tags: template.tags || []
    };

    if (template.id) {
        metadata.templateId = template.id;
    }

    return metadata;
};

/**
 * Build upload data for bulk upload
 * @param {number} vehicleId - Vehicle ID
 * @param {Object} uploadedPhotos - Object with photo name as key and { file, template } as value
 * @returns {Object} { files: File[], metadata: Object[] }
 */
export const buildBulkUploadData = (vehicleId, uploadedPhotos) => {
    const photoEntries = Object.entries(uploadedPhotos);

    const files = photoEntries.map(([, photoData]) => photoData.file);

    const metadata = photoEntries.map(([key, photoData]) => {
        const separatorIndex = key.indexOf('::');
        const photoName = separatorIndex !== -1 ? key.slice(0, separatorIndex) : key;

        const subTag =
            photoData.subTag != null
                ? photoData.subTag
                : photoData.template?.activeSubTag != null
                    ? photoData.template.activeSubTag
                    : separatorIndex !== -1
                        ? key.slice(separatorIndex + 2)
                        : null;

        const meta = {
            photoName,
            subTag: subTag || null,
            category: photoData.template.category,
            description: photoData.template.description,
            tags: photoData.template.tags || []
        };

        if (photoData.template.id) {
            meta.templateId = photoData.template.id;
        }

        return meta;
    });

    return { files, metadata };
};

/**
 * Get the best preview URL from a photo object
 * @param {Object} photo - Photo object with various URL properties
 * @returns {string} The best available preview URL
 */
export const getBestPreviewUrl = (photo) => {
    return photo.thumbnail_url || photo.square_url || photo.four_three_url || photo.original_url;
};

/**
 * Count photos by category
 * @param {Array} photos - Array of photos
 * @param {string} category - Category to count
 * @returns {number} Count of photos in category
 */
export const countPhotosByCategory = (photos, category) => {
    return photos.filter(photo => photo.category === category).length;
};
